/**
 * Zustand store for tracking manually posted vouchers.
 *
 * When a user manually posts a JV or PV through VoucherComposer, the result
 * is stored here so that other components (like ChequesTab) can check if a
 * voucher was manually posted even though the BPV backend status hasn't changed.
 *
 * The store is keyed by BPV ID to support multiple BPVs in the same session.
 */

import { create } from 'zustand';
import type { PostTallyVoucherResponse } from '@/lib/api/tallyLedgers';

interface ManualVoucherState {
  /** Map of BPV ID → manual JV posting result */
  manualJvResults: Record<string, PostTallyVoucherResponse>;
  /** Map of BPV ID → manual PV posting result */
  manualPvResults: Record<string, PostTallyVoucherResponse>;
  /** Set the manual JV result for a BPV */
  setManualJvResult: (bpvId: string, result: PostTallyVoucherResponse) => void;
  /** Set the manual PV result for a BPV */
  setManualPvResult: (bpvId: string, result: PostTallyVoucherResponse) => void;
  /** Get the manual JV result for a BPV */
  getManualJvResult: (bpvId: string) => PostTallyVoucherResponse | undefined;
  /** Get the manual PV result for a BPV */
  getManualPvResult: (bpvId: string) => PostTallyVoucherResponse | undefined;
  /** Clear all manual results (e.g., on logout) */
  clearAll: () => void;
}

export const useManualVoucherStore = create<ManualVoucherState>((set, get) => ({
  manualJvResults: {},
  manualPvResults: {},

  setManualJvResult: (bpvId, result) =>
    set((state) => ({
      manualJvResults: { ...state.manualJvResults, [bpvId]: result },
    })),

  setManualPvResult: (bpvId, result) =>
    set((state) => ({
      manualPvResults: { ...state.manualPvResults, [bpvId]: result },
    })),

  getManualJvResult: (bpvId) => get().manualJvResults[bpvId],

  getManualPvResult: (bpvId) => get().manualPvResults[bpvId],

  clearAll: () => set({ manualJvResults: {}, manualPvResults: {} }),
}));
